import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, X, ImagePlus, Camera } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getListingImages } from "@shared/clothing";

const DEFAULT_MAX_PHOTOS = 8;

interface ManagedItem {
  id: string;
  name?: string | null;
  imageUrl?: string | null;
  imagesJson?: unknown;
}

// Add/remove/reorder photos on a single listing, saved to imagesJson (first =
// cover, mirrored into imageUrl). Self-contained so it doesn't touch the main
// publish form. Works for any listing. maxPhotos lets portfolio/gallery makers
// carry more example photos than a standard product listing.
export function ListingPhotoManager({
  item,
  onOpenChange,
  onSaved,
  maxPhotos = DEFAULT_MAX_PHOTOS,
}: {
  item: ManagedItem | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  maxPhotos?: number;
}) {
  const MAX_PHOTOS = maxPhotos;
  const { toast } = useToast();
  const albumRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setPhotos(getListingImages(item));
  }, [item?.id]);

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    const toUpload = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, Math.max(0, room));
    if (toUpload.length === 0) {
      toast({ title: `You can add up to ${MAX_PHOTOS} photos`, variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append("image", f, f.name || "photo.jpg");
        const res = await fetch("/api/uploads/item-image", {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) throw new Error("Upload failed");
        const url = (await res.json()).imageUrl;
        if (url) uploaded.push(url);
      }
      setPhotos((p) => [...p, ...uploaded]);
    } catch (e: any) {
      toast({
        title: "Couldn't add photo",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (albumRef.current) albumRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const removeAt = (i: number) => setPhotos((p) => p.filter((_, idx) => idx !== i));
  const makeCover = (i: number) =>
    setPhotos((p) => (i === 0 ? p : [p[i], ...p.filter((_, idx) => idx !== i)]));

  const save = async () => {
    if (!item) return;
    setSaving(true);
    try {
      await apiRequest("PUT", `/api/items/${item.id}`, {
        imagesJson: photos,
        imageUrl: photos[0] || "",
      });
      toast({ title: "Photos saved" });
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Couldn't save photos",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Photos</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Add photos to show your range. The first photo is the cover buyers see first.
        </p>

        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((url, i) => (
              <div
                key={`${url}-${i}`}
                className="relative aspect-square overflow-hidden rounded-lg border"
              >
                <img src={url} alt="" className="h-full w-full object-cover" />
                {i === 0 ? (
                  <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] text-white">
                    Cover
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => makeCover(i)}
                    className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] text-white"
                  >
                    Make cover
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label="Remove photo"
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          ref={albumRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => uploadFiles(e.target.files)}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => uploadFiles(e.target.files)}
        />

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => albumRef.current?.click()}
            disabled={uploading || photos.length >= MAX_PHOTOS}
          >
            <ImagePlus className="mr-1 h-4 w-4" /> Add from album
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading || photos.length >= MAX_PHOTOS}
          >
            <Camera className="mr-1 h-4 w-4" /> Camera
          </Button>
        </div>
        {uploading && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || uploading}>
            {saving ? "Saving…" : "Save photos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
